import i18n from 'i18next';

import {
  GetCurrentUserTenantQuery,
  GetJobQuery,
} from '../../generated-mesh/graphql';
import { GetUserProfileQuery } from '../../graphql-mesh/migration-union-types';
import { CandidateProfile } from './FindTalentTemplate';
import { ImplementationType } from './FindTalentControls/types';
import { generateJobLink } from '../../utils/route';

type GetRecruiterByUserUuidQuery =
  import('../../graphql-mesh/migration-union-types').GetRecruiterByUserUuidQuery;

export const DEFAULT_JOBS_LIMIT = 25;
/**
 * The default number of candidates ids to fetch from the
 * recommender in one batch.
 */
export const DEFAULT_CANDIDATE_BATCH_SIZE = 250;
/**
 * The default number of candidates to display on the
 * page at a time.
 */
export const DEFAULT_CANDIDATES_LIMIT = 25;
export const DEFAULT_SAVED_ONLY_STATE = false;
export const DEFAULT_TRAINABLE_STATE = false;
export const MAX_SKILLS_DISPLAY = 10;
export const SEARCH_JOBS_INPUT_STYLES =
  'p-4 pl-11 outline-0 !border-[1px] !border-solid !border-transparent text-black w-full font-primary disabled:cursor-not-allowed font-light text-xs disabled:bg-dawn-30 rounded lg:rounded-l lg:rounded-r-none focus:rounded-r focus:shadow-1 focus:border-none focus:outline-0 focus:ring-offset-0 focus:ring-1 focus:ring-purple';

type getRecruiterOutreachEmailSubjectProps = {
  implementationType: ImplementationType;
  recruiterUserProfile?: GetRecruiterByUserUuidQuery['getRecruiterByUserUuid'];
  selectedJobData: GetJobQuery | undefined;
};

/**
 * Get email subject for recruiter outreach email
 * @param implementationType - is this component in the business services or recruiter implementation
 * @param recruiterUserProfile
 * @param selectedJobData - if a current job is in scope when contacting talent user
 * @param talentFirstName
 * @returns
 */
export const getRecruiterOutreachEmailSubject = ({
  implementationType,
  recruiterUserProfile,
  selectedJobData,
}: getRecruiterOutreachEmailSubjectProps) => {
  const recruiterEmailSubject = i18n.t(
    'contact_candidate.recruiters.outreach_email.subject',
    'Open Role at {{employerName}}',
    {
      employerName: recruiterUserProfile?.employer?.name,
    }
  );

  const recruiterJobSelectedEmailSubject = i18n.t(
    'contact_candidate.recruiters.outreach_email.subject_job_selected',
    'Open Role at {{employerName}}',
    {
      employerName: selectedJobData?.getJob?.company,
    }
  );

  const businessServicesNoJobSelectedEmailSubject = i18n.t(
    'contact_candidate.business_services.outreach_email.subject_no_job_selected',
    'Opportunities that match your background'
  );

  const businessServicesJobSelectedEmailSubject = i18n.t(
    'contact_candidate.business_services.outreach_email.subject_job_selected',
    'Open Role at {{employerName}}',
    {
      employerName: selectedJobData?.getJob?.company,
    }
  );

  if (implementationType === 'recruiter' && !selectedJobData) {
    return recruiterEmailSubject;
  } else if (implementationType === 'recruiter' && selectedJobData) {
    return recruiterJobSelectedEmailSubject;
  } else if (implementationType === 'business_services' && selectedJobData) {
    return businessServicesJobSelectedEmailSubject;
  } else if (implementationType === 'business_services' && !selectedJobData) {
    return businessServicesNoJobSelectedEmailSubject;
  } else {
    return 'Something went wrong.';
  }
};

type getRecruiterOutreachEmailBodyProps = {
  implementationType: ImplementationType;
  loggedInUserProfile: GetUserProfileQuery | undefined;
  loggedInUserTenant: GetCurrentUserTenantQuery | undefined;
  recruiterUserProfile?: GetRecruiterByUserUuidQuery['getRecruiterByUserUuid'];
  selectedJobData: GetJobQuery | undefined;
  talentProfile: CandidateProfile | undefined;
};

const getEmailBody = ({
  selectedJobData,
  options,
  selectedJobKey,
  selectedJobDefaultValue,
  noJobKey,
  noJobDefaultValue,
}: {
  selectedJobData: GetJobQuery | undefined;
  options: {
    employerName: string | undefined;
    companyName: string | undefined;
    loggedInUserFirstName: string | undefined;
    roleName: string | undefined;
    talentFirstName: string | undefined;
    recruiterFirstName: string | undefined;
    tenantName: string | undefined;
    jobUrl: string | undefined;
  };
  selectedJobKey: string;
  selectedJobDefaultValue: string;
  noJobKey: string;
  noJobDefaultValue: string;
}) => {
  if (selectedJobData)
    return i18n.t(selectedJobKey, selectedJobDefaultValue, options);
  return i18n.t(noJobKey, noJobDefaultValue, options);
};

/**
 * Get email body for recruiter outreach email
 * @param implementationType - is this component in the business services or recruiter implementation
 * @param recruiterUserProfile // partners-service
 * @param selectedJobData - if a current job is in scope when contacting talent user
 * @param talentFirstName
 * @returns
 */
export const getRecruiterOutreachEmailBody = ({
  implementationType,
  loggedInUserProfile,
  loggedInUserTenant,
  recruiterUserProfile,
  selectedJobData,
  talentProfile,
}: getRecruiterOutreachEmailBodyProps) => {
  const opts = {
    employerName: recruiterUserProfile?.employer?.name,
    companyName: selectedJobData?.getJob?.company,
    loggedInUserFirstName: loggedInUserProfile?.getUserProfile?.first_name,
    roleName: selectedJobData?.getJob?.job_title,
    talentFirstName: talentProfile?.first_name,
    recruiterFirstName: recruiterUserProfile?.user?.first_name,
    tenantName: loggedInUserTenant?.getCurrentUserTenant?.name,
    jobUrl: (() => {
      const jobId = selectedJobData?.getJob?.job_id;
      const tenantCode = loggedInUserTenant?.getCurrentUserTenant?.code;
      if (!jobId || !tenantCode) return '';
      const url = new URL(generateJobLink({ jobId, tenantCode }));
      url.searchParams.set('utm_source', 'recruiter_email');
      return encodeURIComponent(url.toString());
    })(),
  };

  if (implementationType === 'recruiter') {
    return getEmailBody({
      selectedJobData,
      options: opts,
      selectedJobKey:
        'contact_candidate.recruiters.outreach_email.body_job_selected',
      selectedJobDefaultValue:
        "Hi {{talentFirstName}},%0D%0DI'm {{loggedInUserFirstName}}, and I support recruitment at {{employerName}}. I came across your profile and wanted to reach out about our {{roleName}} position, linked below.%0D%0DYour experience caught my attention, and I think there could be a strong fit here. I'd love to connect and share more about the role and our team.%0D%0DLet me know if you'd like to chat.%0D%0DJob: {{jobUrl}}%0D%0DBest,%0D{{loggedInUserFirstName}}",
      noJobKey: 'contact_candidate.recruiters.outreach_email.body',
      noJobDefaultValue:
        "Hi {{talentFirstName}}, %0D%0DI'm {{recruiterFirstName}}, and I support recruitment at {{employerName}}. I came across your profile and thought you might be a great fit for some roles we're currently hiring for. %0D%0DWe have several positions open that align with your background. I'd love to share more details if you're open to exploring new opportunities. %0D%0DAre you available for a quick chat this week? %0D%0DBest, %0D{{recruiterFirstName}}",
    });
  }

  if (implementationType === 'business_services') {
    return getEmailBody({
      selectedJobData,
      options: opts,
      selectedJobKey:
        'contact_candidate.business_services.outreach_email.body_job_selected',
      selectedJobDefaultValue:
        "Hi {{talentFirstName}},%0D%0DI'm {{loggedInUserFirstName}} from {{tenantName}}. I came across your profile and wanted to reach out about the {{roleName}} position at {{companyName}}, linked below.%0D%0DYour background caught my attention, and I think there could be a strong fit here. I'd love to connect and share more about the role and the team.%0D%0DLet me know if you'd like to chat.%0D%0DJob: {{jobUrl}}%0D%0DBest,%0D{{loggedInUserFirstName}}",
      noJobKey:
        'contact_candidate.business_services.outreach_email.body_no_job_selected',
      noJobDefaultValue:
        "Hi {{talentFirstName}},%0D%0DI'm {{loggedInUserFirstName}} from {{tenantName}}. I came across your profile and wanted to reach out. I think you'd be a strong fit for several roles that our hiring partners are looking to fill.%0D%0DI'd love to connect and share more about these opportunities if you're open to exploring what's out there.%0D%0DLet me know if you'd like to chat.%0D%0DBest,%0D{{loggedInUserFirstName}}",
    });
  }

  throw Error('Unexpected implementation type: ' + implementationType);
};

type buildEmailProps = {
  implementationType: ImplementationType;
  loggedInUserProfile: GetUserProfileQuery | undefined;
  loggedInUserTenant: GetCurrentUserTenantQuery | undefined;
  recruiterUserProfile?: GetRecruiterByUserUuidQuery['getRecruiterByUserUuid'];
  selectedJobData: GetJobQuery | undefined;
  talentProfile: CandidateProfile | undefined;
};

export const buildEmail = ({
  implementationType,
  loggedInUserProfile,
  loggedInUserTenant,
  recruiterUserProfile,
  selectedJobData,
  talentProfile,
}: buildEmailProps) => {
  const emailSubject = getRecruiterOutreachEmailSubject({
    implementationType,
    recruiterUserProfile,
    selectedJobData,
  });

  const emailBody = getRecruiterOutreachEmailBody({
    implementationType,
    loggedInUserProfile,
    loggedInUserTenant,
    recruiterUserProfile,
    selectedJobData,
    talentProfile,
  });

  return { emailSubject, emailBody };
};

/**
 * Formats the given location to a string like:
 * "City, Division, Country".
 *
 * For example, "Toronto, ON, CA" or "BC, CA" or "Ontario, Canada".
 */
export const formatRestrictedLocation = ({
  city,
  division,
  country,
}: {
  city?: string;
  /**
   * The division code or label.
   */
  division?: string;

  /**
   * The country code or label.
   */
  country?: string;
}) => {
  const locations = [city, division, country];

  return locations.filter(Boolean).join(', ');
};
